import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Trophy } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { getMyVenues } from '../services/venueService';
import { sessionBasePath } from '../utils/sessionNavigation';

export default function CreateOpenPlay({ activityType }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const delegated = user?.role !== 'CourtOwner' && user?.hasOpenPlayOrganiserAccess;
  const { id } = useParams();
  const isEditing = Boolean(id);
  const [searchParams] = useSearchParams();
  const copyFrom = !isEditing ? searchParams.get('copyFrom') : null;
  const [copySourceDate, setCopySourceDate] = useState('');
  const [copyError, setCopyError] = useState('');
  const [copyExternal, setCopyExternal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [venues, setVenues] = useState([]);
  const [courts, setCourts] = useState([]);
  const [selectedCourtIds, setSelectedCourtIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    venueId: '', name: '', description: '', activityType: activityType || 'OpenPlay', instructorName: '', equipmentNotes: '', equipmentRentalFee: '',
    date: new Date().toISOString().split('T')[0],
    startTime: '08:00', endTime: '12:00',
    maxPlayers: 16, pricePerPlayer: '', skillLevel: '',
    recurringWeeks: 1, isPrivate: false, requiresJoinApproval: false, blocksCourtBookings: true,
  });

  useEffect(() => {
    (async () => {
      setLoading(true); setCopyError('');
      try {
        const vData = await getMyVenues(delegated);
        setVenues(Array.isArray(vData) ? vData : []);
        if (isEditing || copyFrom) {
          const response = await api.get(copyFrom ? `/openplays/${copyFrom}/copy-settings` : `/openplays/${id}`);
          const openPlay = response.data;
          const allowedVenue = vData.some(v => (v.id || v._id) === openPlay.venueId);
          if (copyFrom && openPlay.venueId && !allowedVenue) throw new Error('The original venue is no longer available for creation.');
          if (copyFrom) setCopyExternal(!openPlay.venueId);
          if (copyFrom) setCopySourceDate(String(openPlay.sourceDate).slice(0, 10));
          setForm({
            venueId: openPlay.venueId || '',
            externalVenueName: openPlay.externalVenueName || '', externalVenueAddress: openPlay.externalVenueAddress || '',
            externalVenueCity: openPlay.externalVenueCity || '', externalVenueMapUrl: openPlay.externalVenueMapUrl || '',
            externalCourtNames: openPlay.externalCourtNames || '', confirmedVenuePermission: false,
            activityType: openPlay.activityType || 'OpenPlay', instructorName: openPlay.instructorName || '', equipmentNotes: openPlay.equipmentNotes || '', equipmentRentalFee: openPlay.equipmentRentalFee ?? '',
            name: openPlay.name || '',
            description: openPlay.description || '',
            date: copyFrom ? '' : String(openPlay.date).slice(0, 10),
            startTime: String(openPlay.startTime || '').slice(0, 5),
            endTime: String(openPlay.endTime || '').slice(0, 5),
            maxPlayers: String(openPlay.maxPlayers ?? 16),
            pricePerPlayer: String(openPlay.pricePerPlayer ?? ''),
            skillLevel: openPlay.skillLevel || '',
            recurringWeeks: 1,
            isPrivate: openPlay.isPrivate === true,
            requiresJoinApproval: openPlay.requiresJoinApproval === true,
            blocksCourtBookings: openPlay.blocksCourtBookings !== false,
          });
          const available = openPlay.venueId ? await loadCourts(openPlay.venueId) : [];
          const ids = copyFrom ? openPlay.courtIds || [] : (openPlay.courts || []).map(c => c.courtId);
          setSelectedCourtIds(ids.filter(cid => available.some(c => (c.id || c._id) === cid)));
          if (copyFrom && ids.some(cid => !available.some(c => (c.id || c._id) === cid))) {
            setCopyError('Some original courts are no longer available. Select the courts for the new session.');
          }
        }
      } catch (error) { const message = error.response?.data?.message || error.message || 'Failed to load session settings'; setCopyError(message); toast.error(message); }
      finally { setLoading(false); }
    })();
  }, [id, isEditing, delegated, copyFrom]);

  async function loadCourts(venueId) {
    try {
      const res = await api.get(`/venues/${venueId}/courts`);
      const active = (Array.isArray(res.data) ? res.data : []).filter(c => c.isActive !== false);
      setCourts(active);
      return active;
    } catch { setCourts([]); throw new Error('Unable to load courts. Please reload and try again.'); }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (loading || (copyFrom && !copySourceDate)) return;
    if (!form.date || (copyFrom && (form.date === copySourceDate || new Date(`${form.date}T${form.startTime}:00+08:00`).getTime() <= Date.now()))) { toast.error('Choose a new date for the copied session'); return; }
    if (copyFrom && copyError && !selectedCourtIds.length && form.blocksCourtBookings) { toast.error('Select the courts for the copied session'); return; }
    if ((!copyExternal && !form.venueId) || !form.name.trim() || form.pricePerPlayer === '') { toast.error('Fill all required fields'); return; }
    if (copyExternal && (!form.externalVenueName?.trim() || !form.externalVenueAddress?.trim() || !form.externalVenueCity?.trim() || !form.externalCourtNames?.trim() || !form.confirmedVenuePermission)) { toast.error('Complete the external venue details and confirm venue permission'); return; }
    if (!form.startTime || !form.endTime || form.endTime === form.startTime) { toast.error('Choose different start and end times'); return; }
    if (form.activityType === 'StepDance' && form.blocksCourtBookings && selectedCourtIds.length === 0) { toast.error('Select the space reserved for Step Dance'); return; }
    if (form.activityType === 'StepDance' && form.equipmentRentalFee !== '' && (!Number.isFinite(Number(form.equipmentRentalFee)) || Number(form.equipmentRentalFee) <= 0 || Number(form.equipmentRentalFee) > 100000 || Math.abs(Number(form.equipmentRentalFee) * 100 - Math.round(Number(form.equipmentRentalFee) * 100)) > 0.00001)) { toast.error('Enter a positive equipment rental price with up to two decimal places'); return; }
    setSubmitting(true);
    try {
      const payload = {
        equipmentRentalFee: form.activityType === 'StepDance' && form.equipmentRentalFee !== '' ? Number(form.equipmentRentalFee) : null,
        activityType: form.activityType, instructorName: form.instructorName || null, equipmentNotes: form.equipmentNotes || null,
        venueId: form.venueId, name: form.name, description: form.description,
        date: form.date, startTime: form.startTime, endTime: form.endTime,
        maxPlayers: parseInt(form.maxPlayers), pricePerPlayer: parseFloat(form.pricePerPlayer),
        requiresJoinApproval: form.requiresJoinApproval,
        isPrivate: form.isPrivate,
        blocksCourtBookings: form.activityType === 'StepDance' ? form.blocksCourtBookings : Number(form.pricePerPlayer) > 0 || form.blocksCourtBookings,
        skillLevel: form.skillLevel || null,
        recurringWeeks: parseInt(form.recurringWeeks) || 1,
        courtIds: form.activityType === 'StepDance' && !form.blocksCourtBookings ? [] : selectedCourtIds.length > 0 ? selectedCourtIds : null,
      };
      if (isEditing) {
        delete payload.venueId;
        delete payload.recurringWeeks;
        await api.put(`/openplays/${id}`, payload);
      } else {
        if (copyExternal) {
          await api.post('/openplays/proposals', {
            ...payload, venueId: null, pricePerPlayer: 0, courtIds: [],
            externalVenueName: form.externalVenueName, externalVenueAddress: form.externalVenueAddress,
            externalVenueCity: form.externalVenueCity, externalVenueMapUrl: form.externalVenueMapUrl || null,
            manualCourtNames: form.externalCourtNames.split(',').map(s => s.trim()).filter(Boolean),
            confirmedVenuePermission: form.confirmedVenuePermission,
          });
        } else await api.post('/openplays', payload);
      }
      toast.success(isEditing ? `${sessionLabel} updated` : (form.recurringWeeks > 1 ? `${form.recurringWeeks} weekly ${sessionLabel} sessions created` : `${sessionLabel} created`));
      navigate(isEditing ? `${sessionBasePath(form.activityType)}/${id}` : sessionBasePath(form.activityType));
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to ${isEditing ? 'update' : 'create'}`);
    } finally { setSubmitting(false); }
  }

  const sessionLabel = form.activityType === 'StepDance' ? 'Step Dance' : 'Open Play';

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(sessionBasePath(form.activityType))} className="btn-outline"><ArrowLeft className="h-4 w-4 mr-1" /> Back</button>
        <div>
          <h1 className="text-2xl font-bold text-[#1F2937]">{isEditing ? `Edit ${sessionLabel}` : copyFrom ? `Copy ${sessionLabel}` : `New ${sessionLabel}`}</h1>
          <p className="text-sm text-[#6B7280]">{isEditing ? 'Update this session without changing its venue' : 'Create a one-off or weekly session'}</p>
        </div>
      </div>

      {copyFrom && <p role="status" className="rounded-xl bg-teal-50 p-4 text-sm">Copying settings only. Choose a different date and review the venue and courts. Players, payments, invitations, chat and match history are not copied. Normal creation and approval checks apply.</p>}
      {copyError && <p role="alert" className="text-sm text-red-700">{copyError}</p>}
      <div className="card p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          {loading && <p role="status">Loading session settings…</p>}
          <div><label htmlFor="activityType" className="block text-xs text-[#6B7280] mb-1">Activity</label>
            <select id="activityType" className="input-field" value={form.activityType} disabled={isEditing || copyExternal || Boolean(activityType)} onChange={(e) => setForm(p => ({ ...p, activityType: e.target.value }))}>
              <option value="OpenPlay">Pickleball Open Play</option><option value="StepDance">Step Dance</option>
            </select>
          </div>
          {form.activityType === 'StepDance' && <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label htmlFor="instructorName" className="block text-xs text-[#6B7280] mb-1">Instructor</label><input id="instructorName" maxLength={200} className="input-field" value={form.instructorName} onChange={e => setForm(p => ({ ...p, instructorName: e.target.value }))} /></div>
            <div><label htmlFor="equipmentNotes" className="block text-xs text-[#6B7280] mb-1">Equipment / step boards</label><input id="equipmentNotes" maxLength={1000} className="input-field" placeholder="What equipment should dancers bring?" value={form.equipmentNotes} onChange={e => setForm(p => ({ ...p, equipmentNotes: e.target.value }))} /></div>
            <div><label htmlFor="equipmentRentalFee" className="block text-xs text-[#6B7280] mb-1">Equipment rental per dancer (PHP)</label><input id="equipmentRentalFee" type="number" min="0.01" max="100000" step="0.01" className="input-field" placeholder="Leave blank if rental is unavailable" value={form.equipmentRentalFee} onChange={e => setForm(p => ({ ...p, equipmentRentalFee: e.target.value }))} /><p className="mt-1 text-xs text-[#6B7280]">Dancers can bring their own equipment or add this rental fee at booking.</p></div>
            <p className="text-xs text-[#6B7280] md:col-span-2">Set capacity to available floor space. Add cancellation terms and what dancers should bring in Notes.</p>
          </div>}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {!copyExternal && <div>
              <label className="block text-xs text-[#6B7280] mb-1">Venue *</label>
              <select value={form.venueId} disabled={isEditing} onChange={(e) => {
                const id = e.target.value;
                setForm((p) => ({ ...p, venueId: id }));
                setSelectedCourtIds([]);
                if (id) loadCourts(id).catch(error => toast.error(error.message));
              }} className="input-field">
                <option value="">Select venue</option>
                {venues.map((v) => <option key={v._id || v.id} value={v._id || v.id}>{v.name}</option>)}
              </select>
            </div>}
            <div>
              <label className="block text-xs text-[#6B7280] mb-1">Name *</label>
              <input type="text" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} className="input-field" placeholder="Saturday Mixer" />
            </div>
            <div>
              <label className="block text-xs text-[#6B7280] mb-1">Start Date *</label>
              <input type="date" required min={copyFrom ? new Date(Date.now() + 8 * 3600000).toISOString().slice(0, 10) : undefined} value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} className="input-field" />
            </div>
            <div>
              <label className="block text-xs text-[#6B7280] mb-1">Start Time</label>
              <input type="time" value={form.startTime} onChange={(e) => setForm((p) => ({ ...p, startTime: e.target.value }))} className="input-field" />
            </div>
            <div>
              <label className="block text-xs text-[#6B7280] mb-1">End Time</label>
              <input type="time" value={form.endTime} onChange={(e) => setForm((p) => ({ ...p, endTime: e.target.value }))} className="input-field" />
              {form.endTime && form.startTime && form.endTime < form.startTime && <p className="mt-1 text-sm font-medium text-[#0B7F83]">Ends the next day</p>}
            </div>
            <div>
              <label className="block text-xs text-[#6B7280] mb-1">{form.activityType === 'StepDance' ? 'Max Dancers' : 'Max Players'}</label>
              <input type="number" min="2" value={form.maxPlayers} onChange={(e) => setForm((p) => ({ ...p, maxPlayers: e.target.value }))} className="input-field" />
            </div>
            {!isEditing && <div>
              <label className="block text-xs text-[#6B7280] mb-1">Repeat weekly</label>
              <select value={form.recurringWeeks} onChange={(e) => setForm((p) => ({ ...p, recurringWeeks: e.target.value }))} className="input-field">
                <option value="1">Does not repeat</option>
                <option value="4">For 4 weeks</option>
                <option value="8">For 8 weeks</option>
                <option value="12">For 12 weeks</option>
                <option value="26">For 26 weeks</option>
              </select>
            </div>}
            <div>
              <label className="block text-xs text-[#6B7280] mb-1">Skill Level</label>
              <select value={form.skillLevel} onChange={(e) => setForm((p) => ({ ...p, skillLevel: e.target.value }))} className="input-field">
                <option value="">All skill levels</option>
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
                <option value="Professional">Professional</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-[#6B7280] mb-1">{form.activityType === 'StepDance' ? 'Price Per Dancer *' : 'Price Per Person *'}</label>
              <input type="number" step="0.01" min="0" disabled={copyExternal} value={form.pricePerPlayer} onChange={(e) => setForm((p) => ({ ...p, pricePerPlayer: e.target.value }))} className="input-field" placeholder="100" />
            </div>
            {(form.activityType === 'StepDance' || (form.pricePerPlayer !== '' && Number(form.pricePerPlayer) === 0)) && <fieldset className="md:col-span-3 rounded-xl border border-[#D8E4E8] p-4">
              <legend className="px-1 text-sm font-semibold">{form.activityType === 'StepDance' ? 'Session space' : 'Court availability'}</legend>
              <label className="flex items-start gap-3 py-2"><input type="radio" name="courtReservation" checked={form.blocksCourtBookings} onChange={() => setForm(p => ({ ...p, blocksCourtBookings: true }))} /><span><strong className="block text-sm">{form.activityType === 'StepDance' ? 'Use courts' : 'Block courts'}</strong><span className="text-xs text-[#6B7280]">Reserve the selected courts for this session.</span></span></label>
              <label className="flex items-start gap-3 py-2"><input type="radio" name="courtReservation" checked={!form.blocksCourtBookings} onChange={() => setForm(p => ({ ...p, blocksCourtBookings: false }))} /><span><strong className="block text-sm">{form.activityType === 'StepDance' ? 'Use dedicated dance space' : 'Keep courts available'}</strong><span className="text-xs text-[#6B7280]">{form.activityType === 'StepDance' ? 'Use the venue’s dedicated dance area. No courts are reserved or blocked.' : 'Other users can still book these courts. This session does not reserve court time.'}</span></span></label>
            </fieldset>}
            <label className="md:col-span-3 flex items-start gap-3 rounded-xl border border-[#D8E4E8] bg-[#F7FBFC] p-3 cursor-pointer">
              <input type="checkbox" checked={!form.isPrivate} onChange={(e) => setForm((p) => ({ ...p, isPrivate: !e.target.checked }))} className="mt-1 h-4 w-4 accent-[#0B8A92]" />
              <span>
                <span className="block text-sm font-semibold text-[#264D59]">Public {sessionLabel}</span>
                <span className="block text-xs text-[#6B7280] mt-0.5">Public sessions appear to all FREAC players. Private sessions are only visible to the organiser and invited players.</span>
              </span>
            </label>
            <div className="md:col-span-3">
              <label className="block text-xs text-[#6B7280] mb-1">Notes</label>
              <textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} className="input-field" rows={2} placeholder="Optional description..." />
            </div>
          </div>

          {copyExternal && <fieldset className="rounded-xl border p-4 space-y-3"><legend>External venue</legend>
            {['externalVenueName', 'externalVenueAddress', 'externalVenueCity', 'externalVenueMapUrl', 'externalCourtNames'].map((field, i) => <label key={field} className="block text-sm">{['Venue name', 'Address', 'City', 'HTTPS map link (optional)', 'Court names (comma-separated)'][i]}<input className="input-field" value={form[field] || ''} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))} /></label>)}
            <label className="flex items-center gap-3 text-sm"><input type="checkbox" checked={form.confirmedVenuePermission || false} onChange={e => setForm(p => ({ ...p, confirmedVenuePermission: e.target.checked }))} />I have permission or a reservation to use this venue for the new session.</label>
          </fieldset>}
          {copyFrom && <label className="flex items-center gap-3 text-sm"><input type="checkbox" checked={form.requiresJoinApproval} onChange={e => setForm(p => ({ ...p, requiresJoinApproval: e.target.checked }))} />Require organiser approval to join</label>}
          {courts.length > 0 && !(form.activityType === 'StepDance' && !form.blocksCourtBookings) && (
            <div>
              <label className="block text-xs text-[#6B7280] mb-1">{form.activityType === 'StepDance' ? 'Reserved space (required)' : 'Courts (optional, leave blank for all)'}</label>
              <div className="flex flex-wrap gap-2">
                {courts.map((c) => {
                  const cid = c._id || c.id;
                  const isSel = selectedCourtIds.includes(cid);
                  return (
                    <button key={cid} type="button"
                      onClick={() => setSelectedCourtIds((prev) => isSel ? prev.filter((x) => x !== cid) : [...prev, cid])}
                      className={`px-3 py-1.5 rounded-[12px] text-xs font-medium border transition-colors ${isSel ? 'bg-[#264D59] text-white border-[#264D59]' : 'bg-[#F1F5F9] text-[#6B7280] border-[#E2E8F0]'}`}>
                      {c.name} ({c.courtType || 'Court'})
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => navigate(sessionBasePath(form.activityType))} className="btn-outline">Cancel</button>
            <button type="submit" disabled={submitting || loading || Boolean(copyFrom && !copySourceDate)} className="btn-primary">
              {submitting ? (isEditing ? 'Saving...' : 'Creating...') : (isEditing ? 'Save Changes' : (form.recurringWeeks > 1 ? 'Create Weekly Series' : `Create ${sessionLabel}`))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
